import React from 'react';

import { useIntl } from 'react-intl';

import { FilterProductIcon } from '../../../../../common/ui/filters/filter-product-icon';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import SelectFilter from '../../../../../common/ui/filters/select-filter';
import { formatAvatarOptionLabel } from '../../../../../common/ui/filters/select-filter/option-label';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { usePrimaryProduct } from '../../../../../controllers/store/bootstrap';
import { filter as lastUpdatedByFilter } from '../../../../../controllers/store/search/filters/last-updated-by';

import { i18n } from './messages';

const LastUpdatedByFilter = ({
	isFilterInDropdown,
	showPrimaryProductIcon,
	fit = 'default',
	onFilterChange,
	hideOnHoverOut,
	onClearOptions,
}: FilterProps): React.JSX.Element | null => {
	const [primaryProduct] = usePrimaryProduct();
	const { formatMessage } = useIntl();
	const [
		filterData,
		{ loadInitialOptions, setLookupInput, setOption, clearLookupInput, clearOptions },
	] = useSelectFilter({
		filterId: lastUpdatedByFilter.id,
	});

	if (!filterData) {
		return null;
	}

	const {
		isLoading,
		suggestedOptions,
		selectedOptions,
		options,
		isLookupDefined,
		lookupInput,
		isLookupActive,
	} = filterData;

	const groupedOptions = [
		{
			label: formatMessage(i18n.confluenceFilterCreatorLabelSelected),
			options: selectedOptions,
		},
		{
			label: formatMessage(i18n.confluenceFilterCreatorLabelUnselected),
			options: suggestedOptions,
		},
	];

	const label =
		fit === 'compact' && selectedOptions.length > 0 ? '' : formatMessage(i18n.lastUpdatedBy);

	return (
		<SelectFilter
			contentFit={fit}
			canClearFilter
			checkboxIndicators
			onDropdownOpen={loadInitialOptions}
			onDropdownClose={clearLookupInput}
			onSearchInputChange={setLookupInput}
			onSelectChange={(option, ...restArgs) => {
				setOption(option, ...restArgs);
				onFilterChange?.(option);
			}}
			options={isLookupActive ? options : groupedOptions}
			selectedOptions={selectedOptions}
			label={label}
			placeholder={formatMessage(i18n.confluenceFilterCreatorPlaceholder)}
			filterOrControlId={'lastUpdatedBy'}
			isLoading={isLoading}
			isSearchable={isLookupDefined}
			searchInput={lookupInput}
			formatOptionLabel={formatAvatarOptionLabel}
			onClearOptions={() => {
				clearOptions();
				onClearOptions?.({ filterId: lastUpdatedByFilter.id });
			}}
			isFilterInDropdown={isFilterInDropdown}
			iconBefore={
				<FilterProductIcon
					showPrimaryProductIcon={showPrimaryProductIcon}
					primaryProduct={primaryProduct}
				/>
			}
			hideOnHoverOut={hideOnHoverOut}
		/>
	);
};

export default LastUpdatedByFilter;
